import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';

import { UploadCallbacks } from "../CompareBatchNeoPenData/USB/Uploader/USBBatchStrokeUploader";
import { IPageSOBP, isSamePageOnlySOBP, NeoStroke } from '@/neolab-libs/nl-lib3-common';
import { IExamInfo } from '@/repositories/model/transfer/IExamInfo';
import { createImages } from '@/ndp/SobpImageConverter';
import PdfToQAMapper from '@/common/objects/QuestionAnswerToPageMapping/PdfToQAMapper';


export interface GAIBatchStrokeRecognizerItem {
    ncodeRange: { start: IPageSOBP, end: IPageSOBP };
    wholeStrokes: NeoStroke[];
    userCode: string;
}

export default class GAIBatchStrokeRecognizer {

    private examInfoArray: IExamInfo[];
    private items: GAIBatchStrokeRecognizerItem[];
    private callbacks: UploadCallbacks | undefined;
    private progressKey: string;
    private nextIndex: number = 0;
    private testInt: number = 0;
    // no persistent failed-tracking; failures halt without advancing nextIndex

    private log(...args: any[]) {
        // eslint-disable-next-line no-console
        console.log('[GAIBatchStrokeRecognizer]', ...args);
    }

    constructor(examInfoArray: IExamInfo[], items: GAIBatchStrokeRecognizerItem[], params?: { key?: string, callbacks?: UploadCallbacks }) {
        const key = params?.key || 'GAIBatchStrokeRecognizer';
        this.progressKey = `GAIRecognizer:${key}:progress`;
        this.callbacks = params?.callbacks;
        this.nextIndex = this.loadProgress();

        this.examInfoArray = examInfoArray;
        this.items = items;
        this.log('created', { items: this.items?.length ?? 0, nextIndex: this.nextIndex });
    }

    public setCallbacks(callbacks?: UploadCallbacks) {
        this.callbacks = callbacks;
        this.log('setCallbacks');
    }

    private loadProgress(): number {
        // In-memory progress only
        return this.nextIndex || 0;
    }

    private saveProgress(index: number) {
        // In-memory progress only
        this.nextIndex = index;
    }

    // removed failed tracking helpers (loadFailed/saveFailed)

    public resetProgress() {
        // In-memory progress only
        this.nextIndex = 0;
    }
    
    public async recognize(): Promise<{ allSucceeded: boolean; result: Record<string, string>; failedIndices: number[] }> {
        const totalItems = this.items?.length ?? 0;
        this.log('recognize start', { totalItems, startIndex: this.nextIndex });

        const merged: Record<string, string> = {};

        // Track resume state
        const startIndex = this.nextIndex;

        // Phase 1: classify all remaining items and gather into one map
        const combinedCodeToStrokes: Record<string, NeoStroke[]> = {};
        const codeToItemIndex: Record<string, number> = {};

        for (let i = startIndex; i < totalItems; i++) {
            const item = this.items[i];
            try {
                this.log('item classify start', { index: i, userCode: item.userCode });
                const grouped = this.classifyStrokes(item.wholeStrokes, item.ncodeRange, this.examInfoArray, item.userCode);
                const keys = Object.keys(grouped);
                for (const k of keys) {
                    combinedCodeToStrokes[k] = grouped[k];
                    codeToItemIndex[k] = i;
                }
            } finally {
                // classification is a lightweight pre-step; do not update external progress here
                this.log('classified', { index: i });
            }
        }

        // Phase 2/3: group by userCode and process up to 5 users per request
        const userCodeToCodes: Record<string, string[]> = {};
        const userCodeToItemIndices: Record<string, Set<number>> = {};
        for (const code of Object.keys(combinedCodeToStrokes)) {
            const userCode = code.split('-')[0];
            if (!userCodeToCodes[userCode]) userCodeToCodes[userCode] = [];
            userCodeToCodes[userCode].push(code);
            const idx = codeToItemIndex[code];
            if (typeof idx === 'number') {
                if (!userCodeToItemIndices[userCode]) userCodeToItemIndices[userCode] = new Set<number>();
                userCodeToItemIndices[userCode].add(idx);
            }
        }

        const userCodes = Object.keys(userCodeToCodes);

        // 2025-10-16
        // 한번에 요청하는 이미지 수가 5개 이하가 되도록 설정. 그렇지 않으면 이미지 인식률 떨어짐
        let numOfQuestions = 0;
        for (let examIndex = 0; examIndex < this.examInfoArray.length; examIndex++) {
            const questions = this.examInfoArray[examIndex].questions;
            numOfQuestions += questions.length;
        }
        
        let CHUNK_USERS = 1;
        if (numOfQuestions == 1) {
            CHUNK_USERS = 3;
        } else if (numOfQuestions == 2) {
            CHUNK_USERS = 2;
        }
        
        // Progress is tracked per recognition chunk (by users), not per classification
        const totalChunks = Math.ceil(userCodes.length / CHUNK_USERS) || 0;
        this.callbacks?.onStart?.(totalChunks);

        let failedIndices: number[] = [];

        let encounteredError = false;
        let chunkIndex = 0;
        for (let start = 0; start < userCodes.length; start += CHUNK_USERS) {
            const sliceUsers = userCodes.slice(start, start + CHUNK_USERS);
            const codeToStrokesForChunk: Record<string, NeoStroke[]> = {};
            this.callbacks?.onItemStart?.(chunkIndex, undefined as any);
            this.log('chunk start', { index: chunkIndex, users: sliceUsers.length });
            for (const u of sliceUsers) {
                for (const code of userCodeToCodes[u]) {
                    codeToStrokesForChunk[code] = combinedCodeToStrokes[code];
                }
            }
            try {
                const images = await createImages({ codeToStrokes: codeToStrokesForChunk });
/*
                try {
			if (typeof window !== 'undefined' && typeof document !== 'undefined') {
				const codes = Object.keys(images);
				for (const code of codes) {
					const filename = `gai-${code}.png`;
					this.downloadDataUrl(images[code].url, filename);
				}
			}
		} catch (e) {
			console.warn('Failed to auto-download test images:', e);
		} 
*/
                const sliceKeys = Object.keys(images);
                    const sliceBlobs: Blob[] = [];
                    for (const code of sliceKeys) {
                        try {
                            const res = await fetch(images[code].url);
                            const blob = await res.blob();
                            sliceBlobs.push(blob);
                        } catch (e) {
                            console.warn('Failed to convert data URL to blob for code', code, e);
                            sliceBlobs.push(new Blob());
                        }
                    }
                    const part = await this.recognizeImages(sliceBlobs, undefined, sliceKeys);
                    Object.assign(merged, part);
                this.log('chunk recognized (users)', { users: sliceUsers.length, codes: sliceKeys.length });
                // Immediately report success for items of these users
                for (const u of sliceUsers) {
                    const indices = Array.from(userCodeToItemIndices[u] ?? []);
                    for (const idx of indices) {
                        this.callbacks?.onItemSuccess?.(idx, undefined as any);
                        this.log('item success', { index: idx, userCode: this.items[idx]?.userCode });
                    }
                }
                // advance progress per successful chunk
                this.callbacks?.onProgress?.(chunkIndex + 1, totalChunks, []);
                chunkIndex += 1;
            } catch (e) {
                // mark all items related to the users in this chunk as failed, then stop
                const indices = sliceUsers.flatMap(u => Array.from(userCodeToItemIndices[u] ?? []));
                failedIndices = Array.from(new Set(indices));
                for (const idx of failedIndices) {
                    this.callbacks?.onItemError?.(idx, undefined as any, e);
                }
                this.log('chunk error (users)', { users: sliceUsers.length, error: String(e) });
                // report progress up to the last successful chunk and include failures
                this.callbacks?.onProgress?.(chunkIndex, totalChunks, failedIndices);
                encounteredError = true;
                break;
            }
        }

        // Persist resume pointer: advance on full success; on failure, point to first failed item
        if (!encounteredError && failedIndices.length === 0) {
            this.nextIndex = totalItems;
        } else if (failedIndices.length > 0) {
            this.nextIndex = Math.min(...failedIndices);
        } else {
            this.nextIndex = startIndex;
        }
        this.saveProgress(this.nextIndex);
        // Final progress update reflects chunk-based tracking
        const finalProgress = (!encounteredError && failedIndices.length === 0) ? totalChunks : Math.min(chunkIndex, totalChunks);
        this.callbacks?.onProgress?.(finalProgress, totalChunks, failedIndices);
        this.log('progress (final)', { chunksDone: finalProgress, totalChunks, failed: failedIndices });

        if (failedIndices.length === 0) {
            this.callbacks?.onComplete?.({ failedIndices: [] });
            this.log('complete', { failed: [] });
        } else {
            this.callbacks?.onComplete?.({ failedIndices: [...failedIndices] });
            this.log('complete with failures', { failed: failedIndices });
        }

        const allSucceeded = failedIndices.length === 0 && !encounteredError;
        return { allSucceeded, result: merged, failedIndices };
    }

    public async resume(): Promise<{ allSucceeded: boolean; result: Record<string, string>; failedIndices: number[] }> {
        this.nextIndex = this.loadProgress();
        this.log('resume', { nextIndex: this.nextIndex });
        return this.recognize();
    }

    // No separate retry path; failures do not advance nextIndex

    private classifyStrokes(wholeStrokes: NeoStroke[], ncodeRange: { start: IPageSOBP, end: IPageSOBP }, examInfoArray: IExamInfo[], userCode: string) {

        const codeToStrokes = {};

        const sobp: IPageSOBP = {
            section: ncodeRange.start.section,
            owner: ncodeRange.start.owner,
            book: ncodeRange.start.book,
            page: ncodeRange.start.page
        };

        const questionToPageIndexMapping = PdfToQAMapper.readQuestionNameToPageIndex(examInfoArray);
        
        for (let examIndex = 0; examIndex < examInfoArray.length; examIndex++) {
            const type = examInfoArray[examIndex].type;
            const questions = examInfoArray[examIndex].questions;

            for (let questionIndex = 0; questionIndex < questions.length; questionIndex++) {
                let questionText = (examIndex + 1) + "-" + (questionIndex + 1);
                let code = userCode + "-" + examInfoArray[examIndex].code + "-" + (questionIndex + 1);

                const { page } = questionToPageIndexMapping[questionText];

                const contentSobp = { ...sobp, page: sobp.page + page };

                const strokes = wholeStrokes.filter((stroke) => isSamePageOnlySOBP(stroke.sobp, contentSobp));
                
                codeToStrokes[code] = [];
                
                for (const stroke of strokes) {
                    if (stroke.dotArray[0]?.y > 24) {
                        codeToStrokes[code].push(stroke);
                    }

                }
            }
        }

        return codeToStrokes;
    }

    private downloadDataUrl(dataUrl: string, filename: string) {
        try {
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                try { document.body.removeChild(a); } catch {}
            }, 0);
        } catch (e) {
            console.warn('downloadDataUrl failed', e);
        }
    }

    /**
     * Send images to backend to recognize texts using AI. Returns mapping keyed by provided codes.
     */
    private async recognizeImages(images: Blob[], languageCode?: string, keysInOrder?: string[]): Promise<Record<string, string>> {

        this.testInt += 1;
        if (this.testInt == 2) {
//            throw Error("recognizeImage error");
        }
        

        if (!images || images.length === 0) return {};
        const form = new FormData();
        images.forEach((blob, i) => {
            form.append('images', blob, `image_${i + 1}.png`);
        });
        if (keysInOrder && keysInOrder.length) {
            keysInOrder.forEach((k) => form.append('keys', k));
        } else {
            for (let i = 0; i < images.length; i++) form.append('keys', String(i));
        }
        if (languageCode) {
            form.append('languageCode', languageCode);
        }

        const res = await fetch('/api/v1/generativeAIUtil/image-to-strings', {
            method: 'POST',
            body: form,
        });
        if (!res.ok) {
            throw new Error(`Failed to recognize images: ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        return data as Record<string, string>;
    }
    
}